import * as React from 'react'
import { ReactElement, useContext, useMemo, useState } from 'react'
import { Button, makeStyles, Text, Toast, ToastBody, ToastFooter, ToastTitle, tokens, Tooltip } from '@fluentui/react-components'
import { Link } from 'react-router-dom'
import { bundleIcon, DismissSquareFilled, EditOffFilled, EditOffRegular, OpenFilled, OpenRegular } from '@fluentui/react-icons'
import { useTranslation } from 'react-i18next'
import useAxios from '../../hooks/useAxios'
import { useSettings } from '../../hooks/useSettings'
import { useTimeDivisorSetting } from '../../hooks/file/useTimeDivisorSetting'
import { QueueData, QueueStatus } from '../../hooks/queue/queueTypes'
import { QueueToastContentProps } from './queueToastTypes'
import useAppToast from '../../hooks/useAppToast'
import FormattedDateTime from '../Date/FormattedDateTime'
import { TimeFormats } from '../../helpers/dateHelper'
import { mapOverviewItemToTrackTimeSuggestionDto, TimeSuggestionOverviewDto, TimeSuggestionStatus } from '../../pages/Time/BaseTimeSuggestionOverview'
import PopupContext from '../../context/PopupContext'
import useUser from '../../hooks/useUser'
import { AxiosInstance } from 'axios'
import { TrackTimeSuggestionDto } from '../../v2/components/Dialog/Registration/TimeSuggestionMutationDialogBody'
import { markCanceled } from '../../helpers/aiCancelFilter'

const DismissSquareIcon = bundleIcon(DismissSquareFilled, DismissSquareFilled)
const DontTrackIcon = bundleIcon(EditOffFilled, EditOffRegular)
const OpenIcon = bundleIcon(OpenFilled, OpenRegular)

const useStyles = makeStyles({
    row: {
        display: 'flex',
        justifyContent: 'space-between',
        flexDirection: 'row',
        gap: tokens.spacingHorizontalM,
    },
    colorRed: {
        color: tokens.colorStatusDangerForeground3,
    },
    colorBlue: {
        color: tokens.colorPaletteBlueBorderActive,
    },
})

export function onClickDontTrackTime(
    axios: AxiosInstance,
    values: Partial<TrackTimeSuggestionDto>,
    onDismiss: () => void,
    onError: (error: any) => void,
) {
    const url = `/time-suggestion/${values.timeSuggestionId}`
    return () =>
        axios
            .put(
                url,
                { status: TimeSuggestionStatus.NoTracking },
                {
                    headers: { SuppressErrors: 'true' },
                },
            )
            .then(() => {
                onDismiss?.()
            })
            .catch(error => {
                // The toast unmounts on dismiss, aborting this request. AppInsights
                // logs that abort as a status-0 dependency failure unless we mark it.
                if (error?.message === 'canceled' || error?.code === 'ERR_CANCELED') {
                    markCanceled(url)
                    return
                }
                onError(error)
            })
}

type TimeSuggestionQueueData = QueueData<object, TimeSuggestionOverviewDto, object>

export default function TimeSuggestionToastContent(props: QueueToastContentProps): ReactElement {
    const job = props.job as TimeSuggestionQueueData
    const toast = useAppToast()
    const closeToast = () => toast.dismiss(props.id)
    const axios = useAxios()
    const { i18n, t: translate } = useTranslation()
    const { settings } = useSettings()
    const popupContext = useContext(PopupContext)
    const styles = useStyles()
    const timeDivisor = useTimeDivisorSetting()
    const { currentUser } = useUser()

    // Guards against a second press while the first is still in flight. Without it the same suggestion was sent
    // twice and the server rejected the repeat, which the user could only read as "the button does nothing".
    const [pending, setPending] = useState(false)

    const timeMutation = useMemo<Partial<TrackTimeSuggestionDto>>(
        () =>
            job.status?.file?.id
                ? mapOverviewItemToTrackTimeSuggestionDto(job.status, timeDivisor, i18n.language, settings.timeSuggestionDescription, currentUser?.id)
                : undefined,
        [job.status],
    )

    if ([QueueStatus.Dispatch, QueueStatus.Queued, QueueStatus.Delayed].includes(job.status.current) || !timeMutation || !job.status) {
        return (
            <Toast>
                <ToastTitle>{translate('base.loading')}</ToastTitle>
            </Toast>
        )
    }
    if ([QueueStatus.Failed].includes(job.status.current)) {
        return (
            <Toast>
                <ToastTitle>{translate('time.suggestion.title')}</ToastTitle>
            </Toast>
        )
    }

    return (
        <Toast>
            <ToastTitle>{translate('time.suggestion.title')}</ToastTitle>
            <ToastBody>
                <Text weight={'semibold'} className={styles.colorBlue}>
                    {timeMutation.emailInbound ? job.status.senderName : job.status.recipientsName || ''}
                </Text>
                <div className={styles.row}>
                    <FormattedDateTime date={job.status.emailSent} format={TimeFormats.ISO} />
                    <Tooltip relationship={'label'} content={job.status.file.name} withArrow>
                        <Link key={job.status.id} to={`/file/${job.status.file.id}`}>
                            <Text size={200}>{job.status.file.reference}</Text>
                        </Link>
                    </Tooltip>
                </div>
                <Text>{job.status.subject || ''}</Text>
            </ToastBody>
            <ToastFooter className={styles.row}>
                <Tooltip content={translate('time.suggestion.cancel.tooltip')} relationship={'label'}>
                    <Button
                        icon={<DismissSquareIcon />}
                        className={styles.colorRed}
                        appearance={'transparent'}
                        disabled={pending}
                        onClick={closeToast}
                    />
                </Tooltip>
                <Tooltip content={translate('time.suggestion.donttrack.tooltip')} relationship={'label'}>
                    <Button
                        icon={<DontTrackIcon />}
                        appearance={'outline'}
                        disabled={pending}
                        onClick={() => {
                            setPending(true)
                            onClickDontTrackTime(axios, timeMutation, closeToast, error => {
                                console.log(error)
                                // Close on failure too. Leaving the toast up invited another press, which the
                                // server rejects again for the same reason, so the toast never went away.
                                toast.showMessage(translate('time.suggestion.donttrack.failed'), { intent: 'warning' })
                                closeToast()
                            })()
                        }}
                    />
                </Tooltip>
                <Tooltip content={translate('time.suggestion.open.tooltip')} relationship={'label'}>
                    <Button
                        icon={<OpenIcon />}
                        appearance={'primary'}
                        disabled={pending}
                        onClick={() => {
                            popupContext.setShowTimeSuggestionDialog(timeMutation)
                            closeToast()
                        }}
                    />
                </Tooltip>
            </ToastFooter>
        </Toast>
    )
}
